import { BarChart3, Bell, ChevronDown, CreditCard, Globe, Monitor, Smartphone, Sparkles, Users } from "lucide-react";
import LogoSlot from "./LogoSlot";
import { hero } from "../data/content";
import { LOGO_ICON_SRC } from "../constants/brand";

// Nodes arranged in a ring around the central Omnyx hub (hub at 50,50).
const nodes = [
  { icon: Sparkles, label: "Innovation", description: "Homegrown ideas, built for global impact.", x: 50, y: 10 },
  { icon: Users, label: "Community", description: "Feeds, games and rewards for local communities.", x: 78, y: 22 },
  { icon: CreditCard, label: "Payments", description: "Secure payments, payouts and refunds.", x: 90, y: 50 },
  { icon: Bell, label: "Notifications", description: "Real-time alerts, right when they matter.", x: 78, y: 78 },
  { icon: BarChart3, label: "Analytics", description: "Live dashboards and platform insights.", x: 50, y: 90 },
  { icon: Monitor, label: "Web platforms", description: "Portals and dashboards for organizers and admins.", x: 22, y: 78 },
  { icon: Globe, label: "Backend & APIs", description: "The services every Omnyx platform runs on.", x: 10, y: 50 },
  { icon: Smartphone, label: "Mobile apps", description: "Apps people open every day.", x: 22, y: 22 },
];

export default function Hero() {
  return (
    <>
      <section
        id="home"
        className="relative flex min-h-[calc(100svh-3.5rem)] flex-col justify-center overflow-hidden px-6 pb-0 pt-16 md:pt-24"
      >
      {/* Decorative gradient flair behind the headline */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-24 top-10 h-72 w-72 rounded-full bg-omnyx-cyan/20 blur-3xl animate-float motion-reduce:animate-none"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-20 bottom-10 h-80 w-80 rounded-full bg-omnyx-teal/15 blur-3xl animate-float motion-reduce:animate-none"
        style={{ animationDelay: "1.5s" }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          backgroundImage:
            "radial-gradient(circle at 50% 35%, rgba(26,123,122,0.12), transparent 60%)",
        }}
      />

      <div className="relative mx-auto max-w-site text-center">
        <h1 className="relative mx-auto max-w-4xl font-heading text-5xl font-extrabold leading-[1.05] tracking-tightest text-site-ink md:text-[80px]">
          <span className="relative inline-block text-omnyx-teal">
            {hero.titleHighlight}
            <svg
              aria-hidden="true"
              viewBox="0 0 200 12"
              preserveAspectRatio="none"
              className="absolute -bottom-2 left-0 h-3 w-full text-omnyx-cyan"
            >
              <path
                d="M0 8 Q 50 0, 100 6 T 200 4"
                fill="none"
                stroke="currentColor"
                strokeWidth="4"
                strokeLinecap="round"
              />
            </svg>
          </span>
          {hero.titleLead}
        </h1>

        <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-site-muted md:text-xl">
          {hero.subtitle}
        </p>

        <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
          <a
            href={hero.primaryCta.href}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex min-h-[44px] items-center justify-center rounded-full bg-omnyx-teal px-8 py-3 font-semibold text-white shadow-glow-teal transition-all hover:bg-omnyx-teal-dark hover:shadow-glow-teal-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-omnyx-teal btn-sheen"
          >
            {hero.primaryCta.label}
          </a>
          <a
            href={hero.secondaryCta.href}
            className="inline-flex min-h-[44px] items-center justify-center rounded-full px-4 py-3 font-semibold text-site-ink transition-colors hover:text-omnyx-teal focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-omnyx-teal"
          >
            {hero.secondaryCta.label}
          </a>
        </div>
      </div>

      {/* Scroll cue down to the network panel */}
      <a
        href="#hero-network"
        aria-label="Scroll to see the Omnyx network"
        className="relative mx-auto mt-12 flex h-10 w-10 items-center justify-center rounded-full text-site-muted transition-colors hover:text-omnyx-teal focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-omnyx-teal"
      >
        <ChevronDown className="h-5 w-5 animate-bounce motion-reduce:animate-none" />
      </a>
      </section>

      {/* Mint panel: Omnyx as the central hub, with live data flowing out to every app and service */}
      <div
        id="hero-network"
        className="relative mx-auto mt-16 max-w-site overflow-hidden rounded-t-tile bg-site-mint px-6 py-16 md:px-16 md:py-20"
      >
        {/* Subtle dot-grid texture across the panel */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-60"
          style={{
            backgroundImage: "radial-gradient(rgba(14, 18, 17, 0.1) 1px, transparent 1px)",
            backgroundSize: "22px 22px",
          }}
        />

        {/* Soft depth blobs */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -left-10 top-10 h-64 w-64 rounded-full bg-omnyx-cyan/30 blur-3xl"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute right-0 top-24 h-72 w-72 rounded-full bg-omnyx-teal/20 blur-3xl"
        />

        <div className="relative text-center">
          <p className="font-heading text-xs font-bold uppercase tracking-widest text-omnyx-teal">
            One ecosystem
          </p>
          <p className="mx-auto mt-2 max-w-md text-sm text-site-muted md:text-base">
            Every Omnyx product runs on the same connected core.
          </p>
        </div>

        <div className="relative mx-auto mt-10 aspect-square w-full max-w-[480px] sm:max-w-[560px] md:max-w-[640px]">
          {/* Connector lines, animated to read as data flowing to/from the hub */}
          <svg
            aria-hidden="true"
            viewBox="0 0 100 100"
            className="pointer-events-none absolute inset-0 h-full w-full text-omnyx-teal/50"
          >
            {nodes.map((node) => (
              <line
                key={node.label}
                x1={50}
                y1={50}
                x2={node.x}
                y2={node.y}
                stroke="currentColor"
                strokeWidth="0.6"
                strokeLinecap="round"
                strokeDasharray="3 5"
                className="animate-data-flow motion-reduce:animate-none"
              />
            ))}
          </svg>

          {/* Central Omnyx hub */}
          <div
            className="absolute left-1/2 top-1/2 flex h-20 w-20 -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center gap-1 rounded-full bg-white shadow-glow-teal ring-1 ring-black/5 animate-pulse-glow motion-reduce:animate-none md:h-24 md:w-24"
          >
            <LogoSlot
              src={LOGO_ICON_SRC}
              alt="Omnyx"
              className="h-7 w-7 md:h-8 md:w-8"
              placeholderClassName="h-7 w-7 text-omnyx-teal md:h-8 md:w-8"
            />
            <span className="font-heading text-[10px] font-bold text-site-ink">Omnyx</span>
          </div>

          {/* App + service nodes */}
          {nodes.map(({ icon: Icon, label, description, x, y }, i) => (
            <div
              key={label}
              style={{ left: `${x}%`, top: `${y}%` }}
              className="group absolute flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-1.5"
            >
              <button
                type="button"
                aria-describedby={`node-tip-${i}`}
                className="flex h-11 w-11 items-center justify-center rounded-full bg-white shadow-lg ring-1 ring-black/5 transition-shadow hover:shadow-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-omnyx-teal md:h-12 md:w-12"
              >
                <Icon className="h-5 w-5 text-omnyx-teal" />
              </button>
              <span className="hidden whitespace-nowrap font-heading text-[11px] font-semibold text-site-ink sm:block">
                {label}
              </span>

              <div
                role="tooltip"
                id={`node-tip-${i}`}
                className="pointer-events-none absolute top-full z-10 mt-2 w-36 rounded-xl bg-site-ink px-3 py-2 text-center text-[11px] leading-snug text-white opacity-0 shadow-xl transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100"
              >
                {description}
              </div>
            </div>
          ))}
        </div>

        <div className="relative mt-10 flex flex-wrap items-center justify-center gap-3 md:mt-14">
          {["Built in-house", "Live, always on", "One core, every platform"].map((fact) => (
            <span
              key={fact}
              className="rounded-full bg-white/80 px-4 py-2 text-xs font-semibold text-site-ink ring-1 ring-black/5 md:text-sm"
            >
              {fact}
            </span>
          ))}
        </div>
      </div>
    </>
  );
}

